import {Add, Download, Link as LinkIcon, People, Refresh, Search} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import {PRIVATE_ADMIN_M3US, PRIVATE_ADMIN_USERS} from 'config/router/paths';
import {daysLabel, formatDate, isExpired, isSoon} from 'utils/adminManagement';

export function ManagementHeader({section, title, description, onAdd, onExport, model}) {
  return (
    <Stack spacing={1.5} sx={{mb: 2}}>
      <Stack direction="row" spacing={1}>
        <Button
          component={Link}
          to={PRIVATE_ADMIN_M3US}
          variant={section === 'm3us' ? 'contained' : 'outlined'}
          startIcon={<LinkIcon />}
        >
          Enlaces M3U
        </Button>
        <Button
          component={Link}
          to={PRIVATE_ADMIN_USERS}
          variant={section === 'users' ? 'contained' : 'outlined'}
          startIcon={<People />}
        >
          Usuarios
        </Button>
      </Stack>
      <Stack direction={{xs: 'column', md: 'row'}} justifyContent="space-between" gap={2}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={750}>
            {title}
          </Typography>
          <Typography color="text.secondary" sx={{mt: 0.75, maxWidth: 760}}>
            {description}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Button
            onClick={model.load}
            disabled={model.loading}
            startIcon={<Refresh />}
            aria-label="Actualizar datos"
          >
            Actualizar
          </Button>
          <Button onClick={onExport} disabled={!model.generatedAt} startIcon={<Download />}>
            CSV
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={onAdd}>
            {section === 'm3us' ? 'Añadir M3U' : 'Crear usuario'}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

export function SummaryCards({items}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          lg: `repeat(${items.length}, minmax(0, 1fr))`
        },
        gap: 1.5,
        mb: 2
      }}
    >
      {items.map((item) => (
        <Paper
          key={item.label}
          variant="outlined"
          sx={{p: 2, borderTop: '3px solid', borderTopColor: `${item.color || 'primary'}.main`}}
        >
          <Typography variant="body2" color="text.secondary">
            {item.label}
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{my: 0.5, overflowWrap: 'anywhere'}}>
            {item.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {item.hint}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

export function Capacity({assigned, max}) {
  const color = assigned > max ? 'error' : assigned === max ? 'warning' : 'primary';
  return (
    <Box sx={{minWidth: 135, maxWidth: 220}}>
      <Typography variant="body2" fontWeight={700} color={`${color}.main`}>
        {assigned} / {max} pantallas
      </Typography>
      <LinearProgress
        color={color}
        variant="determinate"
        value={max > 0 ? Math.min(100, (assigned / max) * 100) : 0}
        aria-label={`${assigned} pantallas asignadas de ${max}`}
        sx={{my: 0.7, height: 6, borderRadius: 3}}
      />
      <Typography variant="caption" color="text.secondary">
        {assigned > max
          ? `${assigned - max} por encima del límite`
          : `${max - assigned} disponibles`}
      </Typography>
    </Box>
  );
}

export function Expiry({value}) {
  return (
    <Box>
      <Typography variant="body2" noWrap>
        {value ? formatDate(value) : 'Sin fecha'}
      </Typography>
      {value && (
        <Typography
          variant="caption"
          color={
            isExpired(value) ? 'error.main' : isSoon(value) ? 'warning.main' : 'text.secondary'
          }
        >
          {daysLabel(value)}
        </Typography>
      )}
    </Box>
  );
}

export function LinkStatus({link}) {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      <Chip
        size="small"
        label={link.enabled ? 'Activado' : 'Desactivado'}
        color={link.enabled ? 'success' : 'default'}
        variant="outlined"
      />
      {isExpired(link.expiresAt) && (
        <Chip size="small" label="Caducado" color="error" variant="outlined" />
      )}
    </Stack>
  );
}

export function SearchField({value, onChange, label}) {
  return (
    <TextField
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{minWidth: {xs: '100%', md: 280}, flex: 1}}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" />
          </InputAdornment>
        )
      }}
    />
  );
}

export function FilterSelect({label, value, onChange, options}) {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{minWidth: 155, flexGrow: {xs: 1, md: 0}}}
      SelectProps={{native: true}}
    >
      {options.map(([id, text]) => (
        <option key={id} value={id}>
          {text}
        </option>
      ))}
    </TextField>
  );
}

export function ManagementMessages({model}) {
  return (
    <>
      {model.loading && <LinearProgress aria-label="Cargando administración" sx={{mb: 2}} />}
      {model.error && (
        <Alert
          severity="error"
          sx={{mb: 2}}
          action={
            <Button color="inherit" onClick={model.load}>
              Reintentar
            </Button>
          }
        >
          {model.error}
        </Alert>
      )}
      <Snackbar
        open={Boolean(model.feedback)}
        autoHideDuration={6000}
        onClose={() => model.setFeedback(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert
          variant="filled"
          severity={model.feedback?.severity || 'success'}
          onClose={() => model.setFeedback(null)}
        >
          {model.feedback?.message || ''}
        </Alert>
      </Snackbar>
    </>
  );
}

export function EmptyResults({loading, onReset, children}) {
  return (
    <Box sx={{textAlign: 'center', py: 7}}>
      <Typography color="text.secondary" sx={{mb: 1}}>
        {loading ? 'Cargando datos…' : children}
      </Typography>
      {!loading && <Button onClick={onReset}>Restablecer filtros</Button>}
    </Box>
  );
}

ManagementHeader.propTypes = {
  section: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  onAdd: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  model: PropTypes.object.isRequired
};
SummaryCards.propTypes = {items: PropTypes.arrayOf(PropTypes.object).isRequired};
Capacity.propTypes = {assigned: PropTypes.number.isRequired, max: PropTypes.number.isRequired};
Expiry.propTypes = {value: PropTypes.string};
LinkStatus.propTypes = {link: PropTypes.object.isRequired};
SearchField.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired
};
FilterSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired
};
ManagementMessages.propTypes = {model: PropTypes.object.isRequired};
EmptyResults.propTypes = {
  loading: PropTypes.bool,
  onReset: PropTypes.func.isRequired,
  children: PropTypes.node
};
